import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import { useVersionPolling } from '../hooks/useVersionPolling';

type UpdateContextType = {
  isUpdateAvailable: boolean;
  isUpdateModalOpen: boolean;
  currentVersion: string;
  serverVersion: string | null;
  lastChecked: Date | null;
  showUpdateModal: () => void;
  hideUpdateModal: () => void;
  acceptUpdate: () => void;
  dismissUpdate: () => void;
  forceCheck: () => void;
};

const UpdateContext = createContext<UpdateContextType | undefined>(undefined);

type UpdateProviderProps = {
  children: ReactNode;
};

export function UpdateProvider({ children }: UpdateProviderProps) {
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [dismissedUpdate, setDismissedUpdate] = useState(false);

  // Use version polling hook
  const { 
    isUpdateAvailable, 
    serverVersion, 
    lastChecked, 
    currentClientVersion, 
    forceCheck 
  } = useVersionPolling({
    pollIntervalMs: 30 * 60 * 1000, // 30 minutes
    enabled: true,
  });

  // Get current version display string from build-time injection
  const currentVersion = `${__VITE_GIT_COMMIT_HASH__} • ${__VITE_BUILD_TIMESTAMP__}`;
  const serverVersionDisplay = serverVersion ? `${serverVersion.gitCommit} • ${serverVersion.buildDate}` : null;

  // Show modal when update becomes available (unless already dismissed)
  useEffect(() => {
    if (isUpdateAvailable && !dismissedUpdate) {
      setIsUpdateModalOpen(true);
    }
  }, [isUpdateAvailable, dismissedUpdate]);

  const showUpdateModal = () => {
    setIsUpdateModalOpen(true);
  };

  const hideUpdateModal = () => {
    setIsUpdateModalOpen(false);
  };

  const acceptUpdate = () => {
    hideUpdateModal();
    // Simple page reload to get the latest version
    window.location.reload();
  };

  const dismissUpdate = () => {
    hideUpdateModal();
    setDismissedUpdate(true);
    // Set up reminder after 30 minutes
    setTimeout(() => {
      setDismissedUpdate(false);
      if (isUpdateAvailable) {
        setIsUpdateModalOpen(true);
      }
    }, 30 * 60 * 1000); // 30 minutes
  };

  const contextValue: UpdateContextType = {
    isUpdateAvailable,
    isUpdateModalOpen,
    currentVersion,
    serverVersion: serverVersionDisplay,
    lastChecked,
    showUpdateModal,
    hideUpdateModal,
    acceptUpdate,
    dismissUpdate,
    forceCheck,
  };

  return (
    <UpdateContext.Provider value={contextValue}>
      {children}
    </UpdateContext.Provider>
  );
}

export function useUpdate(): UpdateContextType {
  const context = useContext(UpdateContext);
  if (!context) {
    throw new Error('useUpdate must be used within an UpdateProvider');
  }
  return context;
}